import React from "react";
import { Avatar, Badge, Button, Flex, Typography } from "antd";
import { useSelector } from "react-redux";
import { FiPhone } from "react-icons/fi";
import { BsChat } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { setCurrentChat } from "../../../redux/slices/chatSlice";
import { RootState } from "../../../redux/store";
import { checkIfContactSaved, formatSingleChat } from "../../../utils";
import "./index.scss";

const { Text } = Typography;
const PhoneIcon = FiPhone as unknown as React.FC;
const ChatIcon = BsChat as unknown as React.FC;

interface CallCardProps {
  data: any;
  onCallClick: React.Dispatch<React.SetStateAction<any>>;
}

const CallCard: React.FC<CallCardProps> = ({ data, onCallClick }) => {
  const dispatch = useAppDispatch();
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const navigate = useNavigate();
  const { chats } = useSelector((state: RootState) => state.chat);

  const getTime = (data: { time: string | number; date?: string }) => {
    if (!data?.time) return "";

    const timestamp =
      typeof data.time === "string" ? parseInt(data.time, 10) : data.time;
    const callDate = new Date(timestamp);

    if (isNaN(callDate.getTime())) return "";

    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfYesterday = new Date(
      startOfToday.getTime() - 24 * 60 * 60 * 1000,
    );

    let dateLabel: string;

    if (callDate >= startOfToday) {
      dateLabel = "Today";
    } else if (callDate >= startOfYesterday) {
      dateLabel = "Yesterday";
    } else {
      dateLabel = data.date
        ? new Date(data.date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : callDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
    }

    const timePart = callDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${dateLabel} ${timePart}`;
  };

  const getCallType = (
    callDirection: number,
    callStatus: boolean,
    duration: number,
  ) => {
    if (callStatus === false && callDirection === 1) {
      return "Missed Call";
    } else if (callStatus === false && callDirection === 2) {
      return "Unanswered Call";
    } else if (callDirection === 1 && duration > 0) {
      return "Incoming Call";
    } else if (callDirection === 2 && duration > 0) {
      return "Outgoing Call";
    } else {
      return "Call";
    }
  };

  const handleCreateDummyChat = (contact: any) => {
    const newChat = {
      _id: contact?._id,
      isGroup: false,
      groupMembers: [],
      lastMessage: "",
      iseFone: contact?.iseFone2eFone,
      mediaType: null,
      filePath: null,
      firstId: null,
      secondId: null,
      firstUserName: null,
      secondUserName: null,
      name: "",
      firstUserPhoneNumber: contact.destinationNumber,
      secondUserPhoneNumber: null,
      lastUpdate: null,
      lastMsgId: null,
      groupSetting: {
        addOthersMembers: true,
        editGroup: true,
        sendMessage: true,
      },
      pastMembers: [],
      lastUpdateToSort: null,
      sourceNumber: contact?.destinationNumber,
      block: null,
      sourceUserName: null,
      isPin: null,
      unreadCount: 0,
      isMuted: null,
      isArchived: null,
      isNewChat: true,
      savedName: checkIfContactSaved(contact?.destinationNumber, contacts),
    };

    const formattedChat = formatSingleChat(newChat);
    dispatch(setCurrentChat(formattedChat));
  };

  const handleChatClick = (contact: any) => {
    if (!contact.iseFone2eFone) {
      toast.error("You cannot text a non-efone number");
      return;
    }

    const number = contact?.destinationNumber;
    const chatList = chats ?? [];
    const existingConvo = chatList.find(
      (chat: any) =>
        chat?.sourceNumber === number && !chat.isGroup && !chat.isBroadCast,
    );
    if (existingConvo) {
      dispatch(setCurrentChat(existingConvo));
    } else {
      handleCreateDummyChat(contact);
    }
    navigate("/chat");
  };

  return (
    <div className="call-card-wrapper">
      <Avatar
        {...(data?.profileImage ? { src: data.profileImage } : {})}
        className={`avatar ${
          data.iseFone2eFone ? "efone-avatar" : "non-efone-avatar"
        }`}
      >
        <Text className="user-initial">
          {checkIfContactSaved(data?.destinationNumber, contacts).slice(0, 2)}
        </Text>
      </Avatar>
      <Flex vertical className="w-full">
        <Flex align="center" gap={8}>
          <div className="contact-name">
            {checkIfContactSaved(data?.destinationNumber, contacts)}
          </div>
          {data?.iseFone2eFone && <Badge className="free-badge">Free</Badge>}
        </Flex>
        <div className="contact-number">{data?.sourceNumber}</div>
        <div
          className={`call-info ${data.callStatus === false && "missed-call"}`}
        >
          {getCallType(data.callDirection, data.callStatus, data.duration)}{" "}
          {getTime({ time: data.time, date: data.date })}
        </div>
      </Flex>
      <div className="actions-wrapper">
        <Button
          type="text"
          onClick={() => {
            handleChatClick(data);
          }}
          icon={<ChatIcon />}
        />
        <Button
          type="text"
          onClick={() => {
            onCallClick(data);
          }}
          icon={<PhoneIcon />}
        />
      </div>
    </div>
  );
};

export default CallCard;
